import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

// Lazy Icon Component
const LazyIcon = ({ src, alt, className, onError }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={imgRef} className={className}>
      {isInView && (
        <img 
          src={src}
          alt={alt}
          className={`w-full h-full object-contain transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setIsLoaded(true)}
          onError={onError}
        />
      )}
      {!isLoaded && (
        <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
          <div className="animate-pulse w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
      )}
    </div>
  );
};

const IconSelector = ({ selectedIcon, onIconSelect, error }) => {
  const { language } = useLanguage();
  const [availableIcons, setAvailableIcons] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Gerçek icon dosyalarını yükle
  useEffect(() => {
    const icons = [
      { id: 'icons8-accuracy-50', name: 'Doğruluk', filename: 'icons8-accuracy-50.png' },
      { id: 'icons8-address-book-50', name: 'Adres Defteri', filename: 'icons8-address-book-50.png' },
      { id: 'icons8-apple-arcade-50', name: 'Apple Arcade', filename: 'icons8-apple-arcade-50.png' },
      { id: 'icons8-approval-50', name: 'Onay', filename: 'icons8-approval-50.png' },
      { id: 'icons8-approval-50-2', name: 'Onay 2', filename: 'icons8-approval-50-2.png' },
      { id: 'icons8-badge-50', name: 'Rozet', filename: 'icons8-badge-50.png' },
      { id: 'icons8-bishop-50', name: 'Piskopos', filename: 'icons8-bishop-50.png' },
      { id: 'icons8-chain-end-50', name: 'Zincir Ucu', filename: 'icons8-chain-end-50.png' },
      { id: 'icons8-clew-50', name: 'İp Yumağı', filename: 'icons8-clew-50.png' },
      { id: 'icons8-clubs-50', name: 'Sinek', filename: 'icons8-clubs-50.png' },
      { id: 'icons8-conflict-50', name: 'Çakışma', filename: 'icons8-conflict-50.png' },
      { id: 'icons8-diamonds-50', name: 'Karo', filename: 'icons8-diamonds-50.png' },
      { id: 'icons8-favorite-50', name: 'Favori', filename: 'icons8-favorite-50.png' },
      { id: 'icons8-garden-50', name: 'Bahçe', filename: 'icons8-garden-50.png' },
      { id: 'icons8-garden-shears-50', name: 'Bahçe Makası', filename: 'icons8-garden-shears-50.png' },
      { id: 'icons8-greek-helmet-50', name: 'Yunan Miğferi', filename: 'icons8-greek-helmet-50.png' },
      { id: 'icons8-hard-working-50', name: 'Çalışkan', filename: 'icons8-hard-working-50.png' },
      { id: 'icons8-job-50', name: 'İş', filename: 'icons8-job-50.png' },
      { id: 'icons8-joker-50', name: 'Joker', filename: 'icons8-joker-50.png' },
      { id: 'icons8-king-50', name: 'Kral', filename: 'icons8-king-50.png' },
      { id: 'icons8-knight-50', name: 'Şövalye', filename: 'icons8-knight-50.png' },
      { id: 'icons8-knight-shield-50', name: 'Şövalye Kalkanı', filename: 'icons8-knight-shield-50.png' },
      { id: 'icons8-knitted-frog-50', name: 'Örgü Kurbağa', filename: 'icons8-knitted-frog-50.png' },
      { id: 'icons8-maintenance-50', name: 'Bakım', filename: 'icons8-maintenance-50.png' },
      { id: 'icons8-nail-50', name: 'Çivi', filename: 'icons8-nail-50.png' },
      { id: 'icons8-nailer-50', name: 'Çivici', filename: 'icons8-nailer-50.png' },
      { id: 'icons8-newspaper-50', name: 'Gazete', filename: 'icons8-newspaper-50.png' },
      { id: 'icons8-notification-50', name: 'Bildirim', filename: 'icons8-notification-50.png' },
      { id: 'icons8-open-end-wrench-50', name: 'Açık Uçlu Anahtar', filename: 'icons8-open-end-wrench-50.png' },
      { id: 'icons8-pawn-50', name: 'Piyon', filename: 'icons8-pawn-50.png' },
      { id: 'icons8-pitchfork-50', name: 'Yaba', filename: 'icons8-pitchfork-50.png' },
      { id: 'icons8-plow-50', name: 'Pulluk', filename: 'icons8-plow-50.png' },
      { id: 'icons8-pubg-helmet-50', name: 'PUBG Miğferi', filename: 'icons8-pubg-helmet-50.png' },
      { id: 'icons8-queen-50', name: 'Kraliçe', filename: 'icons8-queen-50.png' },
      { id: 'icons8-rook-50', name: 'Kale', filename: 'icons8-rook-50.png' },
      { id: 'icons8-scythe-50', name: 'Tırpan', filename: 'icons8-scythe-50.png' },
      { id: 'icons8-shield-50', name: 'Kalkan', filename: 'icons8-shield-50.png' },
      { id: 'icons8-sickle-50', name: 'Orak', filename: 'icons8-sickle-50.png' },
      { id: 'icons8-spade-50', name: 'Kürek', filename: 'icons8-spade-50.png' },
      { id: 'icons8-spades-50', name: 'Maça', filename: 'icons8-spades-50.png' },
      { id: 'icons8-spartan-helmet-50', name: 'Sparta Miğferi', filename: 'icons8-spartan-helmet-50.png' }
    ];
    setAvailableIcons(icons);
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleIconSelect = (iconId) => {
    onIconSelect(iconId);
    setIsDropdownOpen(false);
  };

  const selectedIconData = availableIcons.find(icon => icon.id === selectedIcon);

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {language === 'tr' ? 'Proje İkonu' : 'Project Icon'}
        <span className="text-red-500 ml-1">*</span>
      </label>
      
      {/* Selected Icon Display */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={`
            w-full flex items-center space-x-3 p-3 border rounded-lg transition-all duration-200
            ${isDropdownOpen 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
            }
            ${error ? 'border-red-500' : ''}
          `}
        >
          <div className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
            {selectedIconData ? (
              <LazyIcon 
                src={`/project-icons/${selectedIconData.filename}`}
                alt={selectedIconData.name}
                className="w-8 h-8"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            ) : (
              <span className="text-gray-400 text-lg">📁</span>
            )}
            <span className="text-xs text-gray-400 hidden">?</span>
          </div>
          
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {selectedIconData ? selectedIconData.name : (language === 'tr' ? 'İkon seçin...' : 'Select icon...')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'tr' ? 'Tıklayarak diğer ikonları görün' : 'Click to see other icons'}
            </p>
          </div>
          
          <div className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
        
        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-[60] max-h-80 overflow-y-auto">
            <div className="grid grid-cols-6 gap-2 p-3">
              {availableIcons.map((icon) => (
                <button
                  key={icon.id}
                  type="button"
                  onClick={() => handleIconSelect(icon.id)}
                  className={`
                    w-12 h-12 flex items-center justify-center rounded-lg border-2 transition-all duration-200 overflow-hidden
                    ${selectedIcon === icon.id
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }
                  `}
                  title={icon.name}
                >
                  <LazyIcon 
                    src={`/project-icons/${icon.filename}`}
                    alt={icon.name}
                    className="w-8 h-8"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span className="text-xs text-gray-400 hidden">?</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default IconSelector;
